import { TaskTypes } from '~/api/types';
import type { LiveStop, PlanStop } from '~/data-classes';

/**
 * Abstract base class for stop/task data classes
 * Defines common properties shared across PlanStop and LiveStop
 *
 * @category Data Classes
 * @abstract
 */

export abstract class BaseStop {
    /**
     * Checks if the id string already starts with 'pickup' or 'delivery'
     */
    static hasTaskTypePrefix(id: string): boolean {
        const prefixes = [TaskTypes.PICKUP, TaskTypes.DELIVERY];
        let isPrefixed = false;
        prefixes.forEach((prefix) => {
            if (id.startsWith(prefix)) {
                isPrefixed = true;
            }
        });
        return isPrefixed;
    }

    /**
     * Normalizes a stop ID or task ID by adding a type prefix if not already present.
     * This is to ensure consistency between pickupDeliveryModel: `taxicab` | `carpooling`
     * API returns taskIds and stopIds inconsistently prefixed with pickup/delivery
     *
     * @param {string} idToNormalize - ID to be normalized.
     * @param {string} type - The type prefix (pickup or delivery) to be added to the stop ID.
     * @return {string} The normalized stop ID with the appropriate type prefix.
     */
    static normalizeTwoPartStopId(idToNormalize: string, type: string) {
        const alreadyPrefixed = BaseStop.hasTaskTypePrefix(idToNormalize);
        if (alreadyPrefixed) {
            return idToNormalize;
        }
        return `${type}_${idToNormalize}`;
    }

    /**
     * The unique identifier for the stop/task
     */
    abstract get id(): string;

    /**
     * The stop id as returned from API
     */
    abstract get apiStopId(): string;

    /**
     * The stop identifier (may differ from id for two-part tasks)
     */
    abstract get stopId(): string;

    /**
     * The task identifier
     */
    abstract get taskId(): string;

    /**
     * The type of the task/stop
     */
    abstract get type(): TaskTypes;

    /**
     * Paired stop for a stop that belongs to two part task
     */
    abstract get pairedStop(): PlanStop | LiveStop | undefined;

    /**
     * Set a paired stop for a stop that belongs to two part task
     */
    abstract set pairedStop(stop: PlanStop | LiveStop | undefined);

    /**
     * Whether this is a two-part task (pickup and delivery)
     */
    abstract get isTwoPart(): boolean | undefined;
}
